import { starterQueryKeys } from '../starters/api';
import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ApiError, apiRequest } from '../api/client';
import { calculateAgeFromBirthdate, getAgeVerificationBranch } from '../auth/birthdateAge';
import { useAuth } from '../auth/AuthProvider';
import { setStoredAuthToken } from '../auth/storage';
import { runPendingAdoption } from '../pages/start/adoptionHandoff';
import { Button, Icon, Modal } from '../components/ui';
import { BirthdatePicker } from '../components/ui/BirthdatePicker';
import { useT } from '../i18n';
import styles from './CompleteProfileGate.module.css';

// Weltenschwelle E6 — Profil-Vervollständigungs-Gate.
//
// Blockierendes Modal, das erscheint, sobald ein eingeloggter Nutzer kein
// Geburtsdatum am Konto hat (user.profile_incomplete === true). Das betrifft
// OAuth-Neukonten (Google/Apple legen kein Geburtsdatum an) sowie Altbestand
// ohne Geburtsdatum, der beim nächsten Login sanft nachgefragt wird.
//
// Es gibt KEINE Aussperrung: das Gate sammelt nur das Geburtsdatum + die
// Alters-/Einwilligungs-Flags und POSTet sie an /api/auth/complete-profile.
// Danach flippt profile_incomplete auf false (auth.refresh()) und das Gate
// verschwindet von selbst.

interface CompleteProfileResponse {
  ok?: boolean;
  profile_incomplete?: boolean;
  token?: string;
  [key: string]: unknown;
}

const FALLBACK_SUPPORT_EMAIL = 'support@lorath.eu';

function readSupportEmail(brand: unknown): string {
  if (brand && typeof brand === 'object') {
    const value = (brand as { supportEmail?: unknown }).supportEmail;
    if (typeof value === 'string' && /^[^\s@]+@[^\s@]+$/.test(value.trim())) return value.trim();
  }
  return FALLBACK_SUPPORT_EMAIL;
}

function apiErrorText(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  if (!error.data || typeof error.data !== 'object' || !('error' in error.data)) return null;
  const message = (error.data as { error: unknown }).error;
  return typeof message === 'string' && message.trim() ? message : null;
}

export function CompleteProfileGate() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useT();
  const [birthdate, setBirthdate] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [parentalConsent, setParentalConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Nur aktiv, wenn eingeloggt UND das Geburtsdatum fehlt. Alle Hooks laufen
  // trotzdem (Reihenfolge stabil) — erst hier wird nichts gerendert.
  const active = auth.status === 'authed' && auth.user?.profile_incomplete === true;
  if (!active) return null;

  // Support-Adresse aus /me (brand.supportEmail), sonst die bekannte Adresse.
  const supportEmail = readSupportEmail(auth.user?.brand);
  const age = calculateAgeFromBirthdate(birthdate);
  const branch = getAgeVerificationBranch(age);
  const birthdateEntered = birthdate.length > 0;
  const isUnder13 = birthdateEntered && branch === 'child_under_13';
  const needsConsent = birthdateEntered && branch === 'teen_13_15';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    if (!birthdate || !confirmed) {
      setError(t('auth.birthdate_required'));
      return;
    }
    // Unter 13: kein Selbst-Abschluss — der Nutzer wird an den Support verwiesen.
    if (isUnder13) return;
    if (needsConsent && !parentalConsent) {
      setError(t('auth.parental_consent_required'));
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const resp = await apiRequest<CompleteProfileResponse>('/api/auth/complete-profile', {
        method: 'POST',
        body: {
          birthdate,
          parental_consent: needsConsent && parentalConsent,
          supervised_account: false,
          remember: true,
        },
      });
      // Der Server rotiert den JWT (frisches age_level) und liefert ihn im Body
      // zurück — wie Login/Register/OAuth. Auf nativen Bearer-Clients trägt der
      // ALTE Token noch age_level:'age_0', und die Inhalts-Gates lesen den Token
      // zuerst (req.user.age_level) → ohne Adoption bliebe der Nutzer nach dem
      // Abschluss auf age_0 hängen. Auf Web ist es dank httpOnly-Cookie ein No-op.
      if (typeof resp.token === 'string' && resp.token) {
        await setStoredAuthToken(resp.token);
      }
      // Erfolgreich → AuthContext neu laden. profile_incomplete flippt auf
      // false, das Gate unmountet automatisch.
      await auth.refresh();
      // Lazy geladen: ein statischer Import zieht den App-QueryClient in jeden
      // Test, der @tanstack/react-query ohne QueryClient mockt (Gruppenchat).
      const { queryClient } = await import('./queryClient');
      await queryClient.invalidateQueries({ queryKey: starterQueryKeys.all });
      // P13: Der OAuth-Weg mit Profil-Vervollständigung (Native) behält die
      // In-Memory-Payload — eine ggf. gemerkte Gast-Probeszene adoptieren (No-op
      // ohne Payload). Die P12-Token-Adoption oben (setStoredAuthToken →
      // auth.refresh) bleibt in Reihenfolge UNVERÄNDERT; die Adoption läuft
      // strikt danach. Bei Erfolg direkt in den frischen Chat.
      const adopted = await runPendingAdoption();
      if (adopted) {
        navigate(`/chat/${adopted.campaign_id}`, { replace: true });
      } else if (auth.user?.newcomer_mode && ['/dashboard', '/first-scene/adventure'].includes(location.pathname)) {
        navigate('/first-scene/adventure', { replace: true });
      }
    } catch (err) {
      setError(apiErrorText(err) ?? t('common.connection_error'));
      setSubmitting(false);
    }
  };

  // W2-B/N-04: Unter 13 war das Gate eine echte Sackgasse — kein Knopf, kein
  // X, kein Zurueck (closable=false). Jetzt gibt es zwei Auswege, aber KEINEN
  // Bypass: Abmelden (Muster LegalReacceptGate.handleLogout) und eine sichtbare
  // Support-Adresse. Der Abschluss-Knopf bleibt unter 13 weg, handleSubmit
  // bricht dort weiterhin ab, und der Server lehnt unter 13 ohnehin ab.
  const handleLogout = () => {
    void auth.logout().catch(() => undefined);
  };

  const footer = isUnder13 ? (
    <Button
      type="button"
      variant="secondary"
      fullWidth
      leadingIcon="back"
      onClick={handleLogout}
      data-testid="complete-profile-logout"
    >
      {t('nav.logout')}
    </Button>
  ) : (
    <Button
      type="submit"
      form="complete-profile-form"
      fullWidth
      loading={submitting}
      disabled={!birthdate || !confirmed || (needsConsent && !parentalConsent)}
    >
      {t('auth.complete_profile_submit')}
    </Button>
  );

  return (
    <Modal
      open
      onClose={() => undefined}
      closable={false}
      initialFocus="none"
      title={t('auth.complete_profile_title')}
      footer={footer}
    >
      <form id="complete-profile-form" className={styles.form} onSubmit={handleSubmit} noValidate>
        <p className={styles.intro}>{t('auth.complete_profile_intro')}</p>
        <BirthdatePicker
          variant="form"
          label={t('profile_age.birthdate_label')}
          hint={t('profile_age.birthdate_hint')}
          name="birthdate"
          value={birthdate}
          onChange={(next) => {
            setBirthdate(next);
            setError(null);
          }}
          required
        />
        {isUnder13 ? (
          <div className={styles.under13} role="status">
            <p className={styles.under13Text}>{t('auth.complete_profile_under13')}</p>
            <a
              className={styles.supportLink}
              href={`mailto:${supportEmail}`}
              data-testid="complete-profile-support"
            >
              <Icon name="mail" size={16} />
              <span>{t('app_review.btn_email_support')}</span>
              <span className={styles.supportAddress}>{supportEmail}</span>
            </a>
          </div>
        ) : (
          <>
            {needsConsent ? (
              <label className={styles.checkboxRow}>
                <input
                  className={styles.checkbox}
                  type="checkbox"
                  checked={parentalConsent}
                  onChange={(event) => {
                    setParentalConsent(event.target.checked);
                    setError(null);
                  }}
                />
                <span>{t('auth.parental_consent_checkbox')}</span>
              </label>
            ) : null}
            <label className={styles.checkboxRow}>
              <input
                className={styles.checkbox}
                type="checkbox"
                checked={confirmed}
                onChange={(event) => {
                  setConfirmed(event.target.checked);
                  setError(null);
                }}
              />
              <span>{t('profile_age.birthdate_confirm')}</span>
            </label>
          </>
        )}
        {error ? (
          <div className={styles.error} role="alert">
            {error}
          </div>
        ) : null}
      </form>
    </Modal>
  );
}
